import {DeployInfo, RequestInfo} from "@khan/shared-core";
import {useLocation} from "@khan/urls";

// These content types actually have no output, so we hide
// their output frame
const noOutput = {
    document: true,
};

// Rewrites a production URL to a kasandbox.org URL
const getBaseURL = (host: string): string => {
    // Filter appspot requests to the correct subdomain on
    // kasandbox.org
    const subdomain = /^(.*)-dot-khan-academy\.appspot\.com/.test(host)
        ? RegExp.$1
        : "www";
    return "https://" + subdomain + ".kasandbox.org";
};

// Returns true if the host is a ZND server
export const isZND = (host: string): boolean => {
    return /prod-znd-/.test(host);
};

// Returns true if the host is a production server (including a ZND)
const isProdServer = (host: string): boolean => {
    return (
        /khanacademy\.org/.test(host) || /khan-academy\.appspot\.com/.test(host)
    );
};

/**
 * A hook that returns a URL on kasandbox.org for a given program content type.
 */
export const useSandboxURL = (contentType: string): string => {
    const location = useLocation();
    const host = location.hostname.toLowerCase();

    const path = `/computer-programming/exec/${contentType}`;

    // These content types actually have no output, so we hide
    // their output frame
    if (contentType in noOutput) {
        return path;
    }

    if (!isProdServer(host) || isZND(host)) {
        return path;
    }

    // In order to make sure requests get sent to the right module and
    // version, we need to add the host as the sub-domain.
    // kasandbox.org will then use this to proxy things to the correct GAE
    // host.
    const url = new URL(path, getBaseURL(host));
    // NOTE: This specific case works with kaLocale because all locales
    // are value language tags.
    url.searchParams.set("lang", RequestInfo.kaLocale);
    url.searchParams.set("v", DeployInfo.staticVersion);
    url.searchParams.set("host", host);
    // This parameter prevents the cookie consent modal from appearing in the
    // iframe.
    url.searchParams.set("embedded", "1");

    return url.toString();
};
